/**
 * Pure helpers extracted from AccountManagementLicensesModal.
 *
 * Lives at module scope so unit tests can import the SAME function the
 * component uses — not a hand-written replica that drifts silently. If you
 * change a predicate here, the production call site picks it up; if you
 * delete the production call site, the corresponding tests still exercise
 * this module, but the absence of the call breaks the surrounding feature
 * test (and a future end-to-end test would catch it).
 */

import type { LicenseInstance } from '~/services/superAdminService'

// Resolved canonical labels (some are computed for legacy/manual licenses).
export type ResolvedBillingPackage =
  | 'six_month'
  | 'monthly'
  | 'none'
  | 'unknown_subscription'
  | 'manual_six_month'
  | 'manual_quarterly'
  | 'manual_yearly'
  | 'manual_monthly'
  | null

/** Values the dialog dropdown can send to the backend. */
export type EditableBillingPackage = 'monthly' | 'six_month' | 'none'

/**
 * Resolve a license to one of the canonical cadence labels for display.
 *
 * Reads `billing_package` from either the top-level field or instance_metadata,
 * then falls back through `package_months`, Stripe billing_interval, and
 * manual_recurring fields. Returns `null` when nothing on the license suggests
 * a cadence.
 */
export function getLicenseBillingPackage(license: LicenseInstance | null): ResolvedBillingPackage {
  if (!license) return null
  const metadata = license.instance_metadata || {}
  // Trim BEFORE the OR so a whitespace-only top-level billing_package doesn't
  // short-circuit the metadata fallback (truthy `'   '` would mask
  // metadata.billing_package = 'monthly' otherwise).
  const trimmedTop =
    typeof license.billing_package === 'string' ? license.billing_package.trim() : ''
  const rawMeta = (metadata as { billing_package?: unknown }).billing_package
  const trimmedMeta = typeof rawMeta === 'string' ? rawMeta.trim() : ''
  const billingPackage = trimmedTop || trimmedMeta
  if (billingPackage) {
    return billingPackage as ResolvedBillingPackage
  }

  const packageMonths = metadata.package_months
  if (typeof packageMonths === 'number' && packageMonths === 6) {
    return 'six_month'
  }
  if (typeof packageMonths === 'string' && packageMonths === '6') {
    return 'six_month'
  }

  const billingInterval = metadata.billing_interval
  const intervalCount = metadata.interval_count
  if (billingInterval === 'month' && (intervalCount === 6 || intervalCount === '6')) {
    return 'six_month'
  }

  if (license.subscription_id) {
    return 'unknown_subscription'
  }

  if (license.manual_recurring) {
    if (license.renewal_interval_months === 6) return 'manual_six_month'
    if (license.renewal_interval_months === 3) return 'manual_quarterly'
    if (license.renewal_interval_months === 12) return 'manual_yearly'
    return 'manual_monthly'
  }

  return null
}

/**
 * Format a resolved cadence label for the admin-facing "Current package" chip.
 *
 * Accepts arbitrary strings (not just the canonical union) because the backend
 * may carry historical or future values we haven't enumerated here yet —
 * the `default` branch falls back to a humanized form of whatever it gets.
 */
export function formatBillingPackageLabel(billingPackage: string | null): string {
  switch (billingPackage) {
    case 'six_month':
      return '6-month plan'
    case 'monthly':
      return 'Monthly plan'
    case 'none':
      return 'Single window'
    case 'manual_six_month':
      return 'Manual 6-month'
    case 'manual_quarterly':
      return 'Manual quarterly'
    case 'manual_yearly':
      return 'Manual yearly'
    case 'manual_monthly':
      return 'Manual monthly'
    case 'unknown_subscription':
      return 'Unknown Stripe package'
    default:
      return billingPackage ? billingPackage.replace(/_/g, ' ') : 'Manual'
  }
}

/**
 * Resolve the current license to one of the three values the dialog dropdown
 * can show. Stripe-linked licenses default to "monthly" (matches Stripe's
 * dropdown options); manual licenses default to "none" so the admin must
 * opt in to monthly slicing — mirrors backend license_has_monthly_credit_refresh.
 *
 * Why coerce `six_month` to `monthly` for manual licenses: the manual dropdown
 * shows ONLY `monthly` and `none`. If we returned `six_month` for an unlinked
 * license whose stored cadence is `six_month` (e.g. a license that was Stripe-
 * linked then unlinked), the browser would silently render the first option
 * while the form value stayed `six_month` — a misleading visual mismatch.
 * Returning `monthly` keeps the displayed option in sync with the form value;
 * `buildBillingPackageUpdate` then no-ops (form === resolved) so the backend
 * value is preserved until the admin explicitly picks `none`.
 */
export function getEditableBillingPackage(license: LicenseInstance | null): EditableBillingPackage {
  const pkg = getLicenseBillingPackage(license)
  if (license?.subscription_id) {
    if (pkg === 'six_month') return 'six_month'
    return 'monthly'
  }
  if (pkg === 'monthly' || pkg === 'six_month') return 'monthly'
  return 'none'
}

/** Subset of the edit form that drives the submit-path predicate. */
export interface BillingPackageEditFormSlice {
  billing_type: 'quota' | 'billable_flow'
  billing_package: EditableBillingPackage
}

/**
 * Decide whether to include `billing_package` in the PATCH payload.
 *
 * Three guards, in order:
 *   1. License is NOT Stripe-linked. Stripe owns the cadence — admin overrides
 *      must never reach the backend from the UI.
 *   2. Billing type is NOT `billable_flow`. Flow plans have no monthly-refresh
 *      concept.
 *   3. The form value differs from the current resolved value.
 *
 * Returns the value to send, or `undefined` to skip the field entirely.
 */
export function buildBillingPackageUpdate(
  editForm: BillingPackageEditFormSlice,
  editingLicense: LicenseInstance | null
): EditableBillingPackage | undefined {
  if (!editingLicense) return undefined
  if (editingLicense.subscription_id) return undefined
  if (editForm.billing_type === 'billable_flow') return undefined
  if (editForm.billing_package === getEditableBillingPackage(editingLicense)) return undefined
  return editForm.billing_package
}
